import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { createElement } from 'react'
import { ElementFactory, QuestionPanelDynamicModel, Serializer } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'
import { styles } from './SurveyPanelDynamic.styles'

const CUSTOM_TYPE = 'surveypaneldynamic'

export class SurveyPanelDynamicModel extends QuestionPanelDynamicModel {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get panelItemTitle (): string {
    return this.getPropertyValue('panelItemTitle') ?? ''
  }

  set panelItemTitle (value: string) {
    this.setPropertyValue('panelItemTitle', value)
  }

  get showGroupTitle (): boolean {
    return this.getPropertyValue('showGroupTitle') ?? false
  }

  set showGroupTitle (value: boolean) {
    this.setPropertyValue('showGroupTitle', value)
  }

  get iconOnlyRemove (): boolean {
    return this.getPropertyValue('iconOnlyRemove') ?? false
  }

  set iconOnlyRemove (value: boolean) {
    this.setPropertyValue('iconOnlyRemove', value)
  }
}

export function registerPanelDynamic (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyPanelDynamicModel(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [
      { name: 'panelItemTitle', type: 'text', category: 'general', default: '' },
      { name: 'showGroupTitle', type: 'boolean', category: 'general', default: false },
      { name: 'iconOnlyRemove', type: 'boolean', category: 'general', default: false }
    ],
    function () {
      return new SurveyPanelDynamicModel('')
    },
    'paneldynamic'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionPanelDynamic, props)
  })
}

export class SurveyQuestionPanelDynamic extends SurveyQuestionElementBase {
  get question (): SurveyPanelDynamicModel {
    return this.questionBase as SurveyPanelDynamicModel
  }

  renderElement (): JSX.Element {
    const panels = this.question.panels
    const addText = this.question.panelAddText ?? 'Add New'
    const itemTitleTemplate = this.question.panelItemTitle
    const showGroupTitle = this.question.showGroupTitle
    const iconOnlyRemove = this.question.iconOnlyRemove
    const panelHeading = (index: number): string =>
      itemTitleTemplate !== '' ? itemTitleTemplate.replace('{n}', String(index + 1)) : `${this.question.title} (${index + 2})`

    const removeButton = (index: number): JSX.Element | null => {
      if (!this.question.canRemovePanel) return null
      if (iconOnlyRemove) {
        if (index === 0) return null
        return (
          <IconButton
            onClick={() => { this.question.removePanelUI(index) }}
            aria-label={`Remove ${panelHeading(index)}`}
            size="small"
            sx={styles.compactRemoveButton}
            data-testid={`${this.question.name}-remove-${index}`}
          >
            <DeleteIcon />
          </IconButton>
        )
      }
      return (
        <IconButton
          onClick={() => { this.question.removePanelUI(index) }}
          aria-label="delete"
          color="primary"
          size="small"
          data-testid={`${this.question.name}-remove-${index}`}
        >
          Delete <DeleteIcon />
        </IconButton>
      )
    }

    return (
      <Box sx={styles.container}>
        {showGroupTitle && (this.question.title !== '' || this.question.description !== '') && (
          <Box sx={styles.groupTitle}>
            {this.question.title !== '' && <Typography variant="body1">{this.question.title}</Typography>}
            {this.question.description !== '' && <Typography variant="body2">{this.question.description}</Typography>}
          </Box>
        )}
        {panels.map((panel, index) => (
          <Paper
            key={panel.id}
            elevation={0}
            className='surveyPanelDynamicPaper'
            sx={iconOnlyRemove ? styles.compactPaper : styles.paper}
            data-testid={`${this.question.name}-panel-${index}`}
          >
            <Box sx={iconOnlyRemove ? styles.compactHeader : styles.header}>
              {iconOnlyRemove
                ? <Typography variant="h5" sx={styles.compactTitle}>{panelHeading(index)}</Typography>
                : (
                  <Box sx={styles.title}>
                    {panelHeading(index)}
                  </Box>
                  )}
              {removeButton(index)}
            </Box>

            {panel.questions.map((q) => (
              <Box key={q.name} sx={styles.questionWrapper}>
                {this.creator.createQuestionElement(q)}
              </Box>
            ))}
          </Paper>
        ))}

        {this.question.canAddPanel && (
          <Button
            endIcon={iconOnlyRemove ? <AddCircleIcon /> : <AddIcon />}
            onClick={() => this.question.addPanelUI()}
            sx={iconOnlyRemove ? styles.compactAddButton : styles.addButton}
            data-testid={`${this.question.name}-add-button`}
          >
            {addText}
          </Button>
        )}
      </Box>
    )
  }
}
